/**
 * What this browser knows about its own session, kept beside the httpOnly
 * refresh cookie the page cannot read.
 *
 * Measured 2026-09-03: every page view by a visitor who had never logged in
 * sent `POST /v1/auth/refresh` (401) and then `POST /v1/auth/logout` (422),
 * two API round trips and two console errors per page, because the auth
 * bootstrap has no way to see whether a refresh cookie exists. This hint is
 * that signal:
 *
 * - `"session"`: a login, registration, password reset or refresh succeeded
 *   here, so a refresh cookie was issued to this browser;
 * - `"none"`: the last refresh was rejected outright, or the user logged out,
 *   so there is nothing to refresh until a new login;
 * - absent: unknown, which is every browser before this shipped and every
 *   browser about to come back from an OAuth redirect. The bootstrap refreshes
 *   exactly as before.
 *
 * The hint never grants anything: the API still decides every refresh. It only
 * lets the client skip a request it knows will fail. Storage may be missing or
 * throw (private windows, blocked site data); every reader and writer treats
 * that as "absent".
 */

export const SESSION_HINT_KEY = "progsity:session";

export type SessionHint = "session" | "none";

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function defaultStorage(): StorageLike | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readSessionHint(storage: StorageLike | null = defaultStorage()): SessionHint | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(SESSION_HINT_KEY);
    return value === "session" || value === "none" ? value : null;
  } catch {
    return null;
  }
}

/** `null` forgets the hint, so the next bootstrap asks the API again. */
export function writeSessionHint(
  value: SessionHint | null,
  storage: StorageLike | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    if (value === null) storage.removeItem(SESSION_HINT_KEY);
    else storage.setItem(SESSION_HINT_KEY, value);
  } catch {
    // Nothing to do: an unwritable hint just means the next visit asks the API.
  }
}
